import CustomDivider from '../../reusable/CustomDivider';
import CustomButton from '../../reusable/CustomButton';
import useUserDetails from '../../hooks/useUserDetails';
import { postRequestUploadUrl, uploadFileToS3Bucket } from '../../../services/uploadService';
import { UploadedFileData, UploadedFileStatus } from '../../../interfaces/UploadedFileDataInterface';
import useUploadResults from '../../hooks/useUploadResults';
import { DATA_UPLOAD_REDUCER_ACTIONS } from '../dataUploadReducer';
import { AxiosError, AxiosProgressEvent } from 'axios';
import useErrorModal from '../../hooks/useErrorModal';
import useUploadCardData from '../../hooks/useUploadCardData';
import useSchools from '../../hooks/useSchools';
import useDemographicCategories from '../../hooks/useDemographicCategories';
import selectionStepsMap, { SELECTION_STEPS, selectionStepsKeysMap, selectionDataKeys } from './selectionStepsConfigs';
import { fileTypes } from '../../../constants';
import ExternalSchoolTag from '../../reusable/ExternalSchoolTag';
import { getSchoolIdsFromSelection, getSchoolSelectionOptions } from './schoolSelection';

import './ReviewSelection.css';

interface ReviewSelectionProps {
  selectionData: {
    [key: string]: string[];
  };
  categoryName: string;
  handleEdit: (stepKey: string) => void;
}

interface SelectedItem {
  id: string;
  label: string;
  isExternal?: boolean;
}

function ReviewSelection({ selectionData, categoryName, handleEdit }: ReviewSelectionProps) {
  const {
    userDetails: { data: userDetails },
  } = useUserDetails();
  const { dispatch, uploadData } = useUploadResults();
  const { handleOpen: handleOpenErrorModal } = useErrorModal();

  const {
    schools: { data: schools },
  } = useSchools();
  const {
    demographicCategories: { data: demographicCategories },
  } = useDemographicCategories();

  const { file, fileName, fileType, fileIdToReplace, handleClose, handleUploadProgress } = useUploadCardData();

  const getOrderedSelectedItems = (key: string, selectedValues: string[]): SelectedItem[] => {
    if (key === SELECTION_STEPS.SCHOOL.key && schools) {
      return getSchoolSelectionOptions(schools)
        .filter((option) => selectedValues.includes(option.value))
        .map((option) => ({ id: option.value, label: option.label, isExternal: option.isExternal }));
    } else if (key === SELECTION_STEPS.DEMOGRAPHIC_TYPES.key && demographicCategories) {
      return demographicCategories
        .map((category) => category.display_name)
        .filter((displayName) => selectedValues.includes(displayName))
        .map((displayName) => ({ id: displayName, label: displayName }));
    }

    return selectedValues.map((value) => ({ id: value, label: value }));
  };

  const getDataForBackend = () => {
    // replace schools with school_ids
    selectionData[selectionDataKeys[SELECTION_STEPS.SCHOOL.key]] = getSchoolIdsFromSelection(
      selectionData[SELECTION_STEPS.SCHOOL.key],
      schools ?? []
    );
    delete selectionData[SELECTION_STEPS.SCHOOL.key];

    // replace demographic category display names with general names
    if (categoryName === selectionStepsMap[fileTypes.STUDENT_DEMOGRAPHICS].fileTypeName) {
      selectionData[SELECTION_STEPS.DEMOGRAPHIC_TYPES.key] = selectionData[SELECTION_STEPS.DEMOGRAPHIC_TYPES.key].map(
        (displayName) => {
          const category = demographicCategories?.find((cat) => cat.display_name === displayName);
          return category?.name || '';
        }
      );
    }

    return selectionData;
  };

  const handleSubmit = async () => {
    let fileData: UploadedFileData = {
      file_id: Date.now().toString(), // placeholder value
      file_name: fileName,
      upload_file_type: fileType,
      status: UploadedFileStatus.UPLOAD,
      statistics: [],
      results_summary: [],
      missing_columns: [],
      user_provided_file_metadata: selectionData,
    };

    if (userDetails) {
      try {
        handleClose();

        let requestData = {
          file_name: fileName,
          user_provided_file_metadata: getDataForBackend(),
          upload_file_type: fileType,
          replaces_file_id: fileIdToReplace || undefined,
        };

        uploadData[fileData.file_id] = {
          file: file,
          requestData,
          fileMetaData: fileData,
        };
        const urlResponse = await postRequestUploadUrl(userDetails.eoaId, userDetails.access_token, requestData);
        let fileId = urlResponse.data.s3_key.split('/').at(-1) as string;

        uploadData[fileData.file_id].uploadUrl = urlResponse.data.presigned_url;

        fileData = {
          ...fileData,
          file_id: fileId,
          status: UploadedFileStatus.UPLOAD,
        };

        if (!fileIdToReplace) {
          dispatch({
            type: DATA_UPLOAD_REDUCER_ACTIONS.UPDATE_UPLOADED_FILES_DATA,
            payload: fileData,
          });
        } else {
          dispatch({
            type: DATA_UPLOAD_REDUCER_ACTIONS.REPLACE_UPLOADED_FILE,
            payload: { idToReplace: fileIdToReplace, newFileData: fileData },
          });
        }

        await uploadFileToS3Bucket(urlResponse.data.presigned_url, file, handleUpload);

        fileData = {
          ...fileData,
          status: UploadedFileStatus.PROCESSING,
        };
        dispatch({
          type: DATA_UPLOAD_REDUCER_ACTIONS.UPDATE_UPLOADED_FILES_DATA,
          payload: fileData,
        });
      } catch (error) {
        fileData.status = UploadedFileStatus.UPLOAD_FAILED;
        dispatch({ type: DATA_UPLOAD_REDUCER_ACTIONS.UPDATE_UPLOADED_FILES_DATA, payload: fileData });
        handleOpenErrorModal('Error occured while uploading file!', error as AxiosError);
      }
    }
  };

  const handleUpload = (progressEvent: AxiosProgressEvent) => {
    if (progressEvent.total) {
      const percentsCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);

      handleUploadProgress(percentsCompleted);
    }
  };

  return (
    <div className="review-selection-step">
      <p>Review and edit if needed, then submit your selections.</p>
      <CustomDivider size="large" orientation="horizontal" />
      <p className="file-type-name">{categoryName}</p>
      {Object.entries(selectionData).map(([key, values]) => (
        <div key={key} className="review-selection-category">
          <div className="review-selection-category-header">
            <p>{selectionStepsKeysMap[key]}</p>
            <CustomButton variant="text" buttonText="Edit" buttonAction={() => handleEdit(key)} />
          </div>
          <ul className="category-selections">
            {getOrderedSelectedItems(key, values).map((item) => (
              <li key={item.id} className="selected-value">
                <span className="review-selection-value">
                  {item.label}
                  {item.isExternal && <ExternalSchoolTag variant="ext" />}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ))}
      <CustomButton
        variant="primary"
        size="medium"
        buttonText="Submit"
        buttonAction={handleSubmit}
        className="step-button"
      />
    </div>
  );
}

export default ReviewSelection;
